import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type {
  FilterType,
  PlatformFilter,
  AIGeneratedFilter,
} from "../types/posts";

export interface UsePostsFiltersReturn {
  activeFilter: FilterType;
  platformFilter: PlatformFilter;
  aiGeneratedFilter: AIGeneratedFilter;
  setActiveFilter: (filter: FilterType) => void;
  setPlatformFilter: (filter: PlatformFilter) => void;
  setAiGeneratedFilter: (filter: AIGeneratedFilter) => void;
  clearAllFilters: () => void;
  hasActiveFilters: boolean;
}

export function usePostsFilters(): UsePostsFiltersReturn {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeFilter, setActiveFilterState] = useState<FilterType>(() => {
    const filter = searchParams.get("filter") as FilterType;
    return filter &&
      [
        "all",
        "pending",
        "approved",
        "published",
        "rejected",
        "scheduled",
      ].includes(filter)
      ? filter
      : "pending";
  });
  const [platformFilter, setPlatformFilter] = useState<PlatformFilter>("all");
  const [aiGeneratedFilter, setAiGeneratedFilter] =
    useState<AIGeneratedFilter>("all");

  const setActiveFilter = (filter: FilterType) => {
    setActiveFilterState(filter);
    setSearchParams({ filter });
  };

  const clearAllFilters = () => {
    setPlatformFilter("all");
    setAiGeneratedFilter("all");
  };

  const hasActiveFilters = useMemo(() => {
    return platformFilter !== "all" || aiGeneratedFilter !== "all";
  }, [platformFilter, aiGeneratedFilter]);

  return {
    activeFilter,
    platformFilter,
    aiGeneratedFilter,
    setActiveFilter,
    setPlatformFilter,
    setAiGeneratedFilter,
    clearAllFilters,
    hasActiveFilters,
  };
}
